<template>
    <Page class="organization-home-page">
        <div class="welcome-header">
            <h1>
                Make <span class="primary">your</span> business intelligent with
                <MyStekzLogo class="logo" />
            </h1>
            <p class="subtitle">
                {{
                    hasSteks
                        ? "From ambition to working processes. Smart automation with AI."
                        : "Let's go from your ambition to smart automation with AI."
                }}
            </p>
        </div>

        <!-- Existing Steks section -->
        <div v-if="hasSteks" class="cards-container">
            <Card
                v-for="stek in steks"
                :key="stek.identity"
                class="navigation-card stek-card"
                @click="navigateToStek(stek)"
            >
                <template #header>
                    <Avatar
                        v-if="stek.logo"
                        :image="stek.logo.preview"
                        class="stek-logo"
                    />
                    <Avatar
                        v-else
                        :label="getInitials(stek.name)"
                        size="xlarge"
                        class="stek-logo-placeholder"
                    />
                </template>
                <template #title>{{ stek.name }}</template>
                <template #subtitle v-if="stek.fqdn">{{ stek.fqdn }}</template>
            </Card>

            <!-- Add new Stek card -->
            <Card
                v-if="canCreateStek"
                class="stek-card navigation-card add-stek"
                @click="openCreateStekModal"
            >
                <template #header>
                    <Avatar icon="pi pi-plus" size="xlarge" />
                </template>
                <template #title>Create New Stek</template>
            </Card>
        </div>

        <!-- Quick actions when no Steks exist or as additional actions when they do -->
        <div class="cards-container">
            <Card
                v-if="!hasSteks && canCreateStek"
                class="navigation-card quick-action-card primary-action"
                @click="openCreateStekModal"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-plus-circle"
                            size="xlarge"
                            class="card-icon primary-icon"
                        />
                        <div class="card-content">
                            <h3>Create your first Stek</h3>
                            <p>
                                A Stek is your collaborative workspace for
                                automation
                            </p>
                        </div>
                    </div>
                </template>
            </Card>

            <Card
                class="navigation-card quick-action-card secondary-action"
                @click="navigateToHelp"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-question-circle"
                            size="xlarge"
                            class="card-icon secondary-icon"
                        />
                        <div class="card-content">
                            <h3>Learn about MyStekz</h3>
                            <p>
                                Explore documentation and tutorials to get
                                started
                            </p>
                        </div>
                    </div>
                </template>
            </Card>

            <Card
                v-if="canCreateNewUsers"
                class="navigation-card quick-action-card tertiary-action"
                @click="navigateToUserManagement"
            >
                <template #content>
                    <div class="card-content-wrapper">
                        <Avatar
                            icon="pi pi-users"
                            size="xlarge"
                            class="card-icon tertiary-icon"
                        />
                        <div class="card-content">
                            <h3>Invite team members</h3>
                            <p>
                                Collaborate with your team on automation
                                projects
                            </p>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- What's New section -->
        <div v-if="whatsNewEntries.length > 0" class="whats-new-section">
            <div class="section-header">
                <h2>What's New</h2>
                <Button
                    label="View all"
                    icon="pi pi-sparkles"
                    severity="secondary"
                    text
                    @click="openWhatsNew"
                />
            </div>
            <div class="whats-new-list">
                <Card
                    v-for="entry in whatsNewEntries"
                    :key="entry.identity"
                    class="whats-new-card"
                    :class="{ unseen: isEntryUnseen(entry.identity) }"
                    @click="openWhatsNewEntry(entry)"
                >
                    <template #header>
                        <img
                            v-if="entry.image_url"
                            :src="entry.image_url"
                            :alt="entry.title"
                            class="entry-thumbnail"
                        />
                        <div v-else class="entry-thumbnail-placeholder">
                            <i class="pi pi-sparkles" />
                        </div>
                    </template>
                    <template #title>
                        <span class="entry-title-text">{{ entry.title }}</span>
                        <span
                            v-if="isEntryUnseen(entry.identity)"
                            class="unseen-badge"
                            >New</span
                        >
                    </template>
                    <template #subtitle>
                        {{ formatEntryDate(entry.created_at) }}
                    </template>
                    <template #content>
                        <p class="entry-body-preview">
                            {{ truncateBody(entry.body) }}
                        </p>
                    </template>
                </Card>
            </div>
        </div>

        <Card class="next-steps">
            <template #title>
                <h2>How a superintelligent business takes shape</h2>
            </template>
            <template #content>
                <div class="workflow-steps">
                    <div
                        class="step"
                        v-for="(step, index) in workflowSteps"
                        :key="index"
                    >
                        <Avatar
                            :label="(index + 1).toString()"
                            size="large"
                            class="step-number"
                        />
                        <div class="step-description">
                            <h3>{{ step.title }}</h3>
                            <p>{{ step.description }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </Page>
</template>

<script setup lang="ts">
import Avatar from "primevue/avatar";
import { computed, onMounted } from "vue";

import Page from "@/components/Page.vue";
import { useOrganizationManager } from "@/composables/organization-manager";
import router from "@/router";
import { Stek, useSteksManager } from "@/stores/steks-store";
import { useUserManager } from "@/stores/user-store";
import {
    WHATS_NEW_PREVIEW_LIMIT,
    useWhatsNewManager,
    useWhatsNewStore,
} from "@/stores/whats-new-store";

import MyStekzLogo from "../assets/mystekz-logo.svg?component";

// Get steks data
const { steks } = useSteksManager();
const { currentOrganizationPermissions } = useUserManager();
const hasSteks = computed(() => steks.value && steks.value.length > 0);
const canCreateStek = computed(() => {
    return currentOrganizationPermissions.value?.can_create_stek ?? false;
});
const canCreateNewUsers = computed(() => {
    return currentOrganizationPermissions.value?.can_manage_users ?? false;
});

const { organization } = useOrganizationManager();

// What's New
const whatsNewStore = useWhatsNewStore();
const { entries: allWhatsNewEntries, unseenEntries } = useWhatsNewManager();
const whatsNewEntries = computed(() =>
    allWhatsNewEntries.value.slice(0, WHATS_NEW_PREVIEW_LIMIT)
);

// initDialog must be called in setup()
whatsNewStore.initDialog();

const isEntryUnseen = (id: string) =>
    unseenEntries.value.some((e) => e.identity === id);

const formatEntryDate = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const truncateBody = (body: string, maxLen = 100) => {
    if (body.length <= maxLen) return body;
    return body.substring(0, maxLen).trimEnd() + "…";
};

const openWhatsNew = () => {
    whatsNewStore.openModal(false);
};

const openWhatsNewEntry = (entry: { identity: string }) => {
    whatsNewStore.openModal(false, entry.identity);
};

onMounted(async () => {
    await Promise.all([
        whatsNewStore.fetchEntries({ limit: WHATS_NEW_PREVIEW_LIMIT }),
        whatsNewStore.fetchUnseenEntries(),
    ]);
});

// Workflow steps data
const workflowSteps = [
    {
        title: "Start with Strategy",
        description:
            "Create your Stek, your central workspace for your organization and describe your strategy",
    },
    {
        title: "Group into domains",
        description:
            "Organize your work into functional areas to keep complexity to a minimum",
    },
    {
        title: "Dive into a domain",
        description:
            "Describe the language of a domain and define the roles and responsibilities",
    },
    {
        title: "Design products and services",
        description: "Define the products and services the domain offers",
    },
    {
        title: "Model your processes",
        description:
            "Get into the details of how these products and services are delivered",
    },
    {
        title: "Kick-start your intelligent business",
        description: "Now it's time to lift-off and build your business",
    },
];

// Action handlers
const openCreateStekModal = () => {
    if (organization.value?.name) {
        router.push({
            name: "createStek",
            params: {
                orgName: organization.value.name,
            },
        });
    }
};

const navigateToHelp = () => {
    router.push({ name: "help" });
};

const navigateToUserManagement = () => {
    if (organization.value?.name) {
        router.push({
            name: "userManagement",
            params: {
                orgName: organization.value.name,
            },
        });
    }
};

// Helper functions for Stek cards
const navigateToStek = (stek: Stek) => {
    if (organization.value?.name) {
        router.push({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stek.name,
            },
        });
    }
};

const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2);
};
</script>

<style lang="scss" scoped>
.organization-home-page {
    max-width: 1200px;

    .welcome-header {
        text-align: center;
        margin-bottom: var(--default-gap);

        h1 {
            font-size: var(--text-xxl);
            display: flex;
            align-items: baseline;
            justify-content: center;
            gap: 0.5rem;
        }

        .logo {
            height: 1.2em;
            width: auto;
        }

        .subtitle {
            color: var(--text-color-secondary);
            margin: 0;
        }
    }

    .navigation-card {
        cursor: pointer;
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-1px);
            background-color: var(--surface-background-mild);
        }
    }

    .stek-card {
        flex-basis: 200px;
        max-width: 200px;
        flex-grow: 0;
        border-bottom: var(--stekz-border-width) solid
            var(--stekz-primary-color);

        :deep(.p-card-header) {
            aspect-ratio: 1 / 1;
            display: flex;
        }

        :deep(.p-card-title) {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :deep(.p-avatar) {
            width: 100%;
            height: 100%;
        }
        .stek-logo {
            margin: auto;
            height: auto;
        }
    }

    .cards-container {
        display: flex;
        flex-wrap: wrap;
        gap: var(--default-gap);
        margin-bottom: var(--default-gap);
        max-width: 100%;
    }

    .quick-action-card {
        border-left: var(--stekz-border-width) solid transparent;
        flex: 1;

        :deep(.p-avatar) {
            aspect-ratio: 1 / 1;
            background-color: transparent;
        }

        .card-content-wrapper {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
        }

        &.primary-action {
            border-left-color: var(--stekz-primary-color);

            .primary-icon {
                // background-color: var(--stekz-primary-color);
                color: var(--stekz-primary-color);
                // color: var(--stekz-white);
            }
        }

        &.secondary-action {
            border-left-color: var(--stekz-secondary-color);

            .secondary-icon {
                color: var(--stekz-secondary-color);
            }
        }

        &.tertiary-action {
            border-left-color: var(--stekz-tertiary-color);

            .tertiary-icon {
                color: var(--stekz-tertiary-color);
            }
        }
    }

    .whats-new-section {
        margin-bottom: var(--default-gap);

        .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.75rem;

            h2 {
                margin: 0;
                font-size: 1.2rem;
            }
        }

        .whats-new-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--default-gap);
        }

        .whats-new-card {
            flex: 1 1 220px;
            max-width: 280px;
            cursor: pointer;
            transition:
                transform 0.2s ease,
                box-shadow 0.2s ease;

            &:hover {
                transform: translateY(-1px);
                background-color: var(--surface-background-mild);
            }

            &.unseen {
                border-top: 3px solid var(--stekz-primary-color);
            }

            :deep(.p-card-header) {
                overflow: hidden;
            }

            .entry-thumbnail {
                width: 100%;
                height: 120px;
                object-fit: cover;
            }

            .entry-thumbnail-placeholder {
                width: 100%;
                height: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--surface-section);
                color: var(--text-color-secondary);
                font-size: 2rem;
            }

            :deep(.p-card-title) {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;

                .entry-title-text {
                    flex: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .unseen-badge {
                    font-size: 0.7rem;
                    font-weight: 700;
                    padding: 0.15em 0.5em;
                    background: var(--stekz-primary-color);
                    color: var(--stekz-white, #fff);
                    white-space: nowrap;
                }
            }

            .entry-body-preview {
                margin: 0;
                font-size: 0.875rem;
                color: var(--text-color-secondary);
                overflow: hidden;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
            }
        }
    }

    .next-steps {
        :deep(.p-card-title) {
            // font-size: 1.5rem;
            text-align: center;
            // color: var(--text-color);
        }

        :deep(.p-card-content) {
            padding: 1.5rem;
        }

        .workflow-steps {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
        }

        .step {
            display: flex;
            align-items: flex-start;
            gap: 1rem;

            .step-number {
                background-color: var(--stekz-primary-color);
                color: var(--stekz-white);
                aspect-ratio: 1 / 1;
            }

            .step-description {
                h3 {
                    margin: 0 0 0.5rem 0;
                    color: var(--text-color);
                    font-size: 1.2rem;
                }

                p {
                    margin: 0;
                    color: var(--text-color);
                }
            }
        }
    }
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
    .organization-home-page {
        padding: 0.5rem;

        .welcome-header h1 {
            font-size: 1.5rem;
        }

        .next-steps .workflow-steps {
            grid-template-columns: 1fr;
        }
    }
}
</style>
